// Shell — owns the sign-in/session state machine and decides what the app
// shows (F-33 fix). App itself stays role-driven and untouched by auth, so
// existing role-prop tests (router.test.ts, designlaw.test.ts, nav tests)
// keep working unchanged; Shell is the new thing that supplies App its role
// from the real /api/me response instead of a hardcoded 'OWNER'.
import type { JSX } from 'preact';
import { App } from './app';
import { SignIn } from './components/SignIn';
import { AssistantShell } from './components/AssistantShell';
import { AssistantEnrolPage } from './components/AssistantEnrolPage';
import { FaultState, NoAccessState } from './components/FaultState';
import { session } from './lib/session';
import { loadSnapshot, snapshot, watchClinicDay, stopClinicDayWatch } from './lib/bootstrap';

// The snapshot loads ONCE, when the session first becomes mapped — not on
// navigation (INV-1: "navigation between pages = 0 network calls") and not on
// every re-render. Shell re-renders on every session change, so the guard is a
// module-level flag rather than an effect hook: two renders of the same
// signed-in session must not produce two loads.
let snapshotStarted = false;

/** Test-only: forget that a snapshot was started. Without this the flag — and
 * the in-flight requests it stands for — leak from one test into the next,
 * which is how a background load from an earlier case ended up landing during
 * a later one and resetting its session. */
export function __resetShellForTests(): void {
  snapshotStarted = false;
  stopClinicDayWatch();
}

export function Shell(): JSX.Element {
  const s = session.value;

  // The phone's one-time set-up page works with nobody signed in.
  if (typeof window !== 'undefined' && window.location.pathname === '/assistant/enrol') {
    return <AssistantEnrolPage />;
  }

  switch (s.status) {
    case 'signed-out':
      snapshotStarted = false;
      stopClinicDayWatch();
      return <SignIn />;
    case 'authenticating':
      // Deliberately minimal — no nav, no clinic data, before /api/me
      // resolves (this phase's brief, point 3).
      return <div class="session-loading">Signing in...</div>;
    case 'no-access':
      return <NoAccessState email={s.me?.email} />;
    case 'fault':
      return <FaultState name="sign-in failed" detail={s.error ?? undefined} />;
    case 'signed-in':
      if (!snapshotStarted) {
        snapshotStarted = true;
        void loadSnapshot();
        // The task list resets at the clinic's midnight even in a tab that
        // never reloads (bootstrap.ts refreshTasksIfNewDay).
        watchClinicDay();
      }
      // s.me and s.me.role are guaranteed by session.ts: this status is only
      // ever set alongside a successful, mapped /api/me response.
      if (s.me!.role === 'ASSISTANT') return <AssistantShell />;
      return <App role={s.me!.role!} />;
    default: {
      const _exhaustive: never = s.status;
      return _exhaustive;
    }
  }
}
